import { Crown, Shield, User as UserIcon } from "lucide-react";
import type { OtpStep } from "@/frontend/types/auth";

const OTP_SESSION_KEY = "otp_login_state";

/**
 * Reads persisted OTP step and email. Backed by localStorage so it survives
 * tab-switch focus refetches that briefly unmount the login view AND any
 * full page reloads HMR might trigger while the user has another tab focused.
 */
export function getOtpSessionFromLocalStorage(): { step: OtpStep; email: string; name: string } {
  try {
    const raw = localStorage.getItem(OTP_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        step: parsed.step ?? "email",
        email: parsed.email ?? "",
        name: parsed.name ?? "",
      };
    }
  } catch {}
  return { step: "email", email: "", name: "" };
}

/**
 * Persists OTP step, email, and name to localStorage on every state change.
 * The useEffect in SplitGridLoginView calls this whenever any of them
 * updates, so a tab-switch remount or page reload always finds the latest
 * value when getOtpSessionFromLocalStorage runs.
 */
export function setOtpSessionInLocalStorage(step: OtpStep, email: string, name: string) {
  localStorage.setItem(OTP_SESSION_KEY, JSON.stringify({ step, email, name }));
}

/** Clears persisted OTP state — call after successful sign-in. */
export function clearOtpSessionFromLocalStorage() {
  localStorage.removeItem(OTP_SESSION_KEY);
}

/**
 * Returns true when `path` is a safe same-origin redirect target — i.e. a
 * single leading `/` followed by anything that isn't another `/` or `\`. This
 * blocks protocol-relative URLs (`//evil.com`) and absolute URLs, both of
 * which would otherwise let an attacker craft `?redirect=...` query strings
 * that pivot users off-origin after the OAuth round-trip.
 */
function isSafeSameOriginPath(path: string): boolean {
  return /^\/(?![/\\])/.test(path);
}

/**
 * Reads the `?redirect=` query param on first render and returns a sanitized
 * destination. Anything that isn't a same-origin path falls back to `/`.
 *
 * Intended for use as a `useState` lazy initializer so it runs exactly once on
 * mount without needing useEffect.
 */
export function consumeRedirectFromUrl(): string {
  const raw = new URLSearchParams(window.location.search).get("redirect");
  if (raw && isSafeSameOriginPath(raw)) return raw;
  return "/";
}

/**
 * Reads `?error=` / `?error_description=` from the URL and clears them in
 * history so a page reload doesn't re-trigger the banner. Returns the message
 * for the caller's initial error state.
 */
export function consumeErrorFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const errorCode = params.get("error");
  if (!errorCode) return null;
  const description = params.get("error_description");
  params.delete("error");
  params.delete("error_description");
  const search = params.toString();
  const newUrl = window.location.pathname + (search ? `?${search}` : "");
  window.history.replaceState({}, "", newUrl);
  return description || errorCode;
}

/** True when the page carries an in-flight OAuth authorize request. */
export function hasOAuthRequest(): boolean {
  const qs = new URLSearchParams(window.location.search);
  return qs.has("client_id") && qs.has("sig");
}

/** Re-enters the authorize endpoint with the original client parameters, dropping the signature extras. */
export function resumeOAuthAuthorize(): void {
  const qs = new URLSearchParams(window.location.search);
  for (const drop of ["sig", "exp", "ba_iat", "ba_pl", "ba_param", "redirect"]) qs.delete(drop);
  window.location.href = `/api/auth/oauth2/authorize?${qs.toString()}`;
}

/**
 * Authentication and Role utility functions.
 */

export function getRoleIcon(role: string) {
  if (role === "owner" || role === "admin") return <Shield className="size-4 text-blue-500" />;
  return <UserIcon className="size-4 text-muted-foreground" />;
}

export function getRoleLabel(role: string) {
  if (role === "owner" || role === "admin") return "Admin";
  if (role === "member") return "Member";
  return role;
}

/** Role hierarchy: owner > admin > member. True iff actor can modify target (change role, remove). */
export function canModifyMember(
  actorRoles: string[],
  targetRole: string,
  isPlatformAdmin: boolean
): boolean {
  if (isPlatformAdmin) return true;
  const rank = (r: string) => ({ owner: 3, admin: 2, member: 1 }[r] ?? 0);
  const actorRank = actorRoles.reduce((max, r) => Math.max(max, rank(r)), 0);
  const targetRoles = targetRole.split(",").map((s) => s.trim()).filter(Boolean);
  const targetRank = targetRoles.reduce((max, r) => Math.max(max, rank(r)), 0);
  return actorRank > targetRank;
}

/** Roles the actor can assign. Only platform admins can assign owner. */
export function assignableRoles(actorRoles: string[], isPlatformAdmin: boolean): ("member" | "admin" | "owner")[] {
  if (isPlatformAdmin) return ["member", "admin", "owner"];
  if (actorRoles.includes("owner")) return ["member", "admin"];
  if (actorRoles.includes("admin")) return ["member", "admin"];
  return [];
}
